/**
 * SeasonalBanner — presentational marketing promo band for the public landing page.
 *
 * Pure props component used by SeasonalBannerSection (live) and admin previews
 * (EditSystemEventTypeModal, AdminSiteSettingsPage). Does not fetch data —
 * those concerns live in the container.
 *
 * Layout contract (promo band, not announcement bar):
 * - Artwork is authored on a PURE WHITE field (art anchored right). The band's
 *   background is a light tint of the event type's Mantine color, and the image
 *   is composited with mix-blend-mode: multiply, so the white field vanishes
 *   into the tint. Changing the event type's color re-skins the banner without
 *   touching the image — the color always matches the event type.
 * - Shell-first paint (Task #1067): tint + copy render as soon as JSON arrives;
 *   the image fades in when bytes load (or stays tint-only if missing/failed).
 * - Dark copy on the light tint; soft tint-colored left-fade as a contrast
 *   fallback for admin-uploaded busy images
 * - Copy wrapped in Mantine Container size="lg" so it aligns with nav/hero
 * - Headline uses the brand display font (Nunito) so the strip reads as a
 *   designed promo, not body text; subtext uses the handwritten accent font
 * - Optional countdown chip when daysUntil is provided
 * - Not dismissible — the strip stays visible whenever the API returns a banner
 */

import { Badge, Container, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { brandFonts } from '../../config/theme';
import { SEASONAL_BANNER_DEFAULT_COLOR } from '../../constants/seasonalBanner';
import classes from './SeasonalBanner.module.css';

export interface SeasonalBannerProps {
  /**
   * Resolved absolute or blob URL for the cover background image.
   * Optional — when empty, the tinted shell still paints so copy is not blocked
   * on media download.
   */
  imageUrl?: string | null;
  /** Primary overlay headline. */
  headline: string;
  /** Optional supporting subtext under the headline. */
  subtext?: string | null;
  /** Mantine color name used for the countdown chip accent. */
  color: string;
  /** Whole days until the holiday; omit for evergreen / no countdown. */
  daysUntil?: number | null;
  /** Holiday display name used in the countdown chip copy. */
  name?: string | null;
  /** Invoked when the visitor activates the CTA area (banner body). */
  onCta?: () => void;
  /** Compact height for admin live previews. */
  compact?: boolean;
}

/**
 * Builds countdown chip label: "{name} is in {n} days" or "today" when 0.
 *
 * @param name Holiday display name.
 * @param daysUntil Non-null day count from the API.
 */
function buildCountdownLabel(name: string | null | undefined, daysUntil: number): string {
  const label = name?.trim() || 'Event';

  if (daysUntil === 0) {
    return `${label} is today`;
  }

  const unit = daysUntil === 1 ? 'day' : 'days';
  return `${label} is in ${daysUntil} ${unit}`;
}

/**
 * Renders the seasonal marketing banner strip.
 */
export function SeasonalBanner({
  imageUrl,
  headline,
  subtext,
  color,
  daysUntil,
  name,
  onCta,
  compact = false,
}: SeasonalBannerProps) {
  const showCountdown = daysUntil != null;
  const isClickable = typeof onCta === 'function';
  const resolvedImageUrl = imageUrl?.trim() || '';
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageReady, setImageReady] = useState(false);

  // Reset fade state when the media URL changes (admin preview swap, holiday flip).
  useEffect(() => {
    setImageReady(false);
  }, [resolvedImageUrl]);

  // Cached images may already be complete before onLoad fires — promote immediately.
  useEffect(() => {
    if (!resolvedImageUrl) return;
    const img = imageRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setImageReady(true);
    }
  }, [resolvedImageUrl]);

  // Only a plain Mantine palette name is safe to interpolate into a CSS var
  // reference; anything unexpected from the API falls back to the default.
  const safeColor = /^[a-z][a-z0-9-]*$/i.test(color?.trim() ?? '')
    ? color.trim()
    : SEASONAL_BANNER_DEFAULT_COLOR;

  // The band background and scrim both derive from the event type's color via
  // this custom property, so recoloring an event type re-skins the banner.
  const bannerStyle = {
    '--banner-tint': `var(--mantine-color-${safeColor}-0)`,
  } as CSSProperties;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onCta?.();
    }
  };

  const markImageReady = () => setImageReady(true);

  return (
    <div
      className={[
        classes.banner,
        compact ? classes.bannerCompact : '',
        isClickable ? classes.bannerClickable : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={bannerStyle}
      role={isClickable ? 'link' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onCta : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      data-testid="seasonal-banner"
    >
      {resolvedImageUrl ? (
        <img
          ref={imageRef}
          className={[classes.background, imageReady ? classes.backgroundReady : '']
            .filter(Boolean)
            .join(' ')}
          src={resolvedImageUrl}
          alt=""
          aria-hidden
          decoding="async"
          // Keep hero LCP preferred — banner art is decorative above the fold.
          fetchPriority="low"
          onLoad={markImageReady}
          onError={markImageReady}
          data-testid="seasonal-banner-image"
        />
      ) : null}
      <div className={classes.scrim} aria-hidden />

      {/* Real Mantine Container so copy aligns with the nav/hero column */}
      <Container size="lg" className={classes.container}>
        <div className={classes.content}>
          <div className={classes.textBlock}>
            {/* Brand display font (matches the hero headline) instead of body text */}
            <Text
              className={classes.headline}
              style={{ fontFamily: brandFonts.headline }}
              lineClamp={2}
            >
              {headline}
            </Text>
            {subtext?.trim() ? (
              // Handwritten accent font keeps the supporting line light and playful
              <Text
                className={classes.subtext}
                style={{ fontFamily: brandFonts.handwritten }}
                lineClamp={2}
              >
                {subtext}
              </Text>
            ) : null}
          </div>

          {showCountdown ? (
            <Badge
              className={classes.chip}
              color={color || 'indigo'}
              variant="filled"
              size="lg"
              radius="xl"
            >
              {buildCountdownLabel(name, daysUntil!)}
            </Badge>
          ) : null}
        </div>
      </Container>
    </div>
  );
}

export default SeasonalBanner;
